import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Tooltip,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface ImageGalleryProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
  title?: string;
  showDownload?: boolean;
  showRotation?: boolean;
  showNavigation?: boolean;
  showZoom?: boolean;
  showFullscreen?: boolean;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  open,
  onClose,
  images,
  currentIndex = 0,
  onIndexChange,
  title = 'Image Gallery',
  showDownload = true,
  showRotation = true,
  showNavigation = true,
  showZoom = true,
  showFullscreen = true,
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const currentImage = images[currentIndex];

  // Reset state when image changes
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
  }, [currentIndex]);

  // Debug zoom changes
  useEffect(() => {
    console.log('Zoom changed:', zoom);
  }, [zoom]);

  // Debug rotation changes
  useEffect(() => {
    console.log('Rotation changed:', rotation);
  }, [rotation]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (showNavigation && currentIndex > 0) {
            onIndexChange?.(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (showNavigation && currentIndex < images.length - 1) {
            onIndexChange?.(currentIndex + 1);
          }
          break;
        case '+':
        case '=':
          if (showZoom) {
            setZoom(prev => Math.min(prev * 1.2, 5));
          }
          break;
        case '-':
          if (showZoom) {
            setZoom(prev => Math.max(prev / 1.2, 0.1));
          }
          break;
        case 'r':
          if (showRotation) {
            setRotation(prev => (prev + 90) % 360);
          }
          break;
        case 'f':
          if (showFullscreen) {
            toggleFullscreen();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, images.length, onClose, onIndexChange, showNavigation, showZoom, showRotation, showFullscreen]);

  const handleZoomIn = useCallback(() => {
    console.log('Zoom in clicked');
    setZoom(prev => {
      const newZoom = Math.min(prev * 1.2, 5);
      console.log('New zoom:', newZoom);
      return newZoom;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    console.log('Zoom out clicked');
    setZoom(prev => {
      const newZoom = Math.max(prev / 1.2, 0.1);
      console.log('New zoom:', newZoom);
      return newZoom;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleRotateLeft = useCallback(() => {
    console.log('Rotate left clicked');
    setRotation(prev => {
      const newRotation = prev - 90;
      const finalRotation = newRotation < 0 ? newRotation + 360 : newRotation;
      console.log('New rotation:', finalRotation);
      return finalRotation;
    });
  }, []);

  const handleRotateRight = useCallback(() => {
    console.log('Rotate right clicked');
    setRotation(prev => {
      const newRotation = (prev + 90) % 360;
      console.log('New rotation:', newRotation);
      return newRotation;
    });
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleDownload = useCallback(() => {
    if (currentImage) {
      const link = document.createElement('a');
      link.href = currentImage;
      link.download = `image-${currentIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [currentImage, currentIndex]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom(prev => Math.max(0.1, Math.min(5, prev * delta)));
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  if (!open || !currentImage) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: '100vw',
          maxHeight: '100vh',
          width: '100vw',
          height: '100vh',
          margin: 0,
          borderRadius: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
        },
      }}
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
            {title} ({currentIndex + 1} of {images.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showFullscreen && (
              <Tooltip title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}>
                <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Close (Esc)">
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Image Container */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                willChange: 'transform',
              }}
            >
              <img
                src={currentImage}
            alt={`Gallery image ${currentIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: imageLoaded ? 'block' : 'none',
                }}
                onLoad={handleImageLoad}
                draggable={false}
              />
              {!imageLoaded && (
                <Box
            sx={{
                    width: 400,
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                  }}
                >
                  <Typography sx={{ color: 'white' }}>Loading...</Typography>
                </Box>
              )}
        </motion.div>
      </AnimatePresence>
        </Box>

      {/* Navigation Arrows */}
        {showNavigation && images.length > 1 && (
        <>
          <IconButton
              onClick={() => onIndexChange?.(currentIndex - 1)}
              disabled={currentIndex === 0}
            sx={{
              position: 'absolute',
                left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
                zIndex: 10,
              color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                '&:disabled': {
                  opacity: 0.3,
              },
            }}
          >
              <PrevIcon />
          </IconButton>
          <IconButton
              onClick={() => onIndexChange?.(currentIndex + 1)}
              disabled={currentIndex === images.length - 1}
            sx={{
              position: 'absolute',
                right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
                zIndex: 10,
              color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                '&:disabled': {
                  opacity: 0.3,
              },
            }}
          >
              <NextIcon />
          </IconButton>
        </>
      )}

        {/* Controls */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {showZoom && (
            <>
              <Tooltip title="Zoom Out (-)">
                <IconButton onClick={handleZoomOut} sx={{ color: 'white' }}>
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetZoom}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                {Math.round(zoom * 100)}%
              </Button>
              <Tooltip title="Zoom In (+)">
                <IconButton onClick={handleZoomIn} sx={{ color: 'white' }}>
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {showRotation && (
            <>
              <Tooltip title="Rotate Left (R)">
                <IconButton onClick={handleRotateLeft} sx={{ color: 'white' }}>
                  <RotateLeftIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate Right (R)">
                <IconButton onClick={handleRotateRight} sx={{ color: 'white' }}>
                  <RotateRightIcon />
        </IconButton>
              </Tooltip>
            </>
          )}

          {showDownload && (
            <Tooltip title="Download">
              <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Thumbnail Strip */}
        {showNavigation && images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
              bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
              zIndex: 10,
            display: 'flex',
            gap: 1,
              maxWidth: '80%',
              overflowX: 'auto',
              pb: 1,
          }}
        >
            {images.map((image, index) => (
            <Box
              key={index}
                onClick={() => onIndexChange?.(index)}
              sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  overflow: 'hidden',
                cursor: 'pointer',
                  border: currentIndex === index ? '2px solid white' : '2px solid transparent',
                  opacity: currentIndex === index ? 1 : 0.7,
                  transition: 'all 0.2s ease',
                '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
          ))}
        </Box>
      )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageGallery;